import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import LanguageSwitcher from '../components/LanguageSwitcher';
import CerimoniaChiave from '../components/CerimoniaChiave';
// deriveKey non compare piu' in questo file: era importato per la riga di
// cambio password che derivava una chiave e la buttava via (vedi il commento
// al PUNTO 7 in saveProfile). Toglierlo dall'import e' il modo di impedire
// che qualcuno la riscriva per abitudine.
import { generateRecoveryToken, deriveRecoveryKey, encryptKeyWithRecovery, hashRecoveryToken, formatBytes } from '../utils/crypto';
import {
  api2faSetup, api2faEnable, api2faDisable,
  apiWebdavStatus, apiWebdavEnable, apiWebdavDisable, apiWebdavRegenerate,
  apiQuota, apiMe,
  apiRecoverySetup, apiUpdateProfile, apiUpdateEmail, apiUpdateUsername, apiCancelSubscription, apiResumeSubscription,
  apiTicketsList, apiTicketsGet, apiTicketsCreate, apiTicketsReply,
  apiReferralsGet, apiReferralsInvite, apiResendVerification
} from '../utils/api';
import FamilyPanel from '../components/FamilyPanel';
import { formatDate, formatDateTime } from '../utils/formatDate';
import {
  isNativeBackupAvailable, enableNativeBackup, disableNativeBackup,
  getNativeBackupStatus, requestNativeMediaPermissions, requestNativeNotificationPermission,
  triggerNativeBackupNow, onNativeBackupProgress,
} from '../utils/nativeBackup';

const API_BASE = process.env.REACT_APP_API_URL || '/api';
// In production REACT_APP_API_URL is just '/api' (nginx proxies same-origin),
// so stripping '/api' from it leaves an empty string — the WebDAV URL shown
// to the user would be a bare "/dav/username/" with no host, which rclone/
// Cyberduck/Finder can't use. Fall back to the page's real origin whenever
// API_BASE isn't already an absolute URL (dev tunnels set an absolute one).
const SERVER_BASE = API_BASE.startsWith('http') ? API_BASE.replace('/api', '') : window.location.origin;

// Che computer sta usando chi guarda. Serve solo a decidere QUALE DELLE DUE
// guide mettere per prima: nessuna delle due viene mai nascosta, perche'
// questa lettura sbaglia (un Mac con Windows in macchina virtuale, un iPad
// che si dichiara Mac) e nascondere quella giusta sarebbe peggio che
// mostrarla seconda.
function sistemaOperativo() {
  const p = `${navigator.userAgentData?.platform || ''} ${navigator.platform || ''} ${navigator.userAgent || ''}`.toLowerCase();
  if (/win/.test(p)) return 'windows';
  if (/mac|iphone|ipad|ipod/.test(p)) return 'mac';
  return null;
}

export default function ProfilePage({ onBack, onApriGuida}) {
  const { t, i18n } = useTranslation('profile');
  const { user, encKey, refreshUser, cambiaPassword } = useAuth();

  // Profile state
  const [profileEdit,   setProfileEdit]   = useState(false);
  const [firstName,     setFirstName]     = useState(user?.firstName || '');
  const [lastName,      setLastName]      = useState(user?.lastName  || '');
  const [emailVal,      setEmailVal]      = useState(user?.email     || '');
  const [curPwd,        setCurPwd]        = useState('');
  const [newPwd,        setNewPwd]        = useState('');
  const [newPwdConf,    setNewPwdConf]    = useState('');
  const [profileMsg,    setProfileMsg]    = useState('');
  const [profileLoading,setProfileLoading]= useState(false);

  // Email change
  const [emailEdit,  setEmailEdit]  = useState(false);
  const [emailInput, setEmailInput] = useState(user?.email || '');
  const [emailMsg,   setEmailMsg]   = useState('');
  const [emailBusy,  setEmailBusy]  = useState(false);

  // Username change — auto-generated from the email at registration (no
  // username field on /register anymore), editable here.
  const [usernameEdit,  setUsernameEdit]  = useState(false);
  const [usernameInput, setUsernameInput] = useState(user?.username || '');
  const [usernameMsg,   setUsernameMsg]   = useState('');
  const [usernameBusy,  setUsernameBusy]  = useState(false);

  // Subscription state
  const [subLoading, setSubLoading] = useState(false);
  const [subMsg,     setSubMsg]     = useState('');

  // Recovery state
  const [recoveryToken,   setRecoveryToken]   = useState('');
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryMsg,     setRecoveryMsg]     = useState('');
  const [recoveryCopied,  setRecoveryCopied]  = useState(false);

  // 2FA state
  const [twofa, setTwofa]         = useState({ enabled: user?.twoFactorEnabled || false });
  const [qrCode, setQrCode]       = useState(null);
  const [secret, setSecret]       = useState('');
  const [tfCode, setTfCode]       = useState('');
  const [tfDisablePwd, setTfDisablePwd] = useState('');
  const [tfMsg, setTfMsg]         = useState('');
  const [tfLoading, setTfLoading] = useState(false);
  const [tfStep, setTfStep]       = useState('idle'); // idle | setup | verify

  // WebDAV state
  const [webdav, setWebdav]       = useState({ enabled: false, token: null });
  const [wdLoading, setWdLoading] = useState(false);
  const [wdMsg, setWdMsg]         = useState('');
  const [showToken, setShowToken] = useState(false);
  // Quale dei tre riquadri e' stato copiato ('url' | 'token' | 'rclone'), per
  // mostrare la conferma SOLO accanto a quello premuto.
  const [wdCopiato, setWdCopiato]   = useState(null);
  const rifUrl   = useRef(null);
  const rifToken = useRef(null);

  // Native (Android app) photo backup — only ever rendered inside the real
  // Capacitor app, never on the regular web site (see utils/nativeBackup.js).
  const isNative = isNativeBackupAvailable();
  const [backupStatus, setBackupStatus] = useState({ enabled: false, hasMediaPermission: false });
  // La quota serve a una cosa sola qui: sapere se "Esegui backup ora" puo'
  // ancora riuscire. Una richiesta all'apertura della scheda, non di piu'.
  const [quota, setQuota] = useState(null);
  useEffect(() => { apiQuota().then(setQuota).catch(() => {}); }, []);
  const muroFile = !!quota && quota.maxFiles > 0 && quota.fileCount >= quota.maxFiles;
  const muroSpazio = !!quota && quota.total > 0 && quota.used >= quota.total;
  const accountAlMuro = muroFile || muroSpazio;
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupMsg, setBackupMsg] = useState('');
  // Cerimonia della chiave prima di attivare il backup foto (2026-09-14, A).
  const [cerimoniaBackup, setCerimoniaBackup] = useState(false);
  const [backupProgress, setBackupProgress] = useState(null); // { total, completed, currentName } | null

  // Referral program
  const [referral, setReferral]           = useState(null);
  const [referralLoading, setReferralLoading] = useState(true);
  const [referralCopied, setReferralCopied]   = useState(false);
  const [friendEmail, setFriendEmail]     = useState('');
  const [referralMsg, setReferralMsg]     = useState('');
  const [referralBusy, setReferralBusy]   = useState(false);
  const [verifyMsg, setVerifyMsg]         = useState('');
  const [verifyBusy, setVerifyBusy]       = useState(false);

  // Support tickets
  const [tickets, setTickets]             = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [newSubject, setNewSubject]       = useState('');
  const [newMessage, setNewMessage]       = useState('');
  const [ticketBusy, setTicketBusy]       = useState(false);
  const [ticketMsg, setTicketMsg]         = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText]         = useState('');

  useEffect(() => {
    apiWebdavStatus().then(setWebdav).catch(() => {});
    apiMe().then(d => { setTwofa({ enabled: d.user?.twoFactorEnabled || false }); refreshUser(d.user); }).catch(() => {});
    loadTickets();
    loadReferral();
    if (isNative) getNativeBackupStatus().then(setBackupStatus).catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!isNative) return;
    return onNativeBackupProgress(({ total, completed, currentName }) => {
      setBackupProgress({ total, completed, currentName });
    });
  }, [isNative]);

  // ── Referral program ─────────────────────────────────────
  function loadReferral() {
    setReferralLoading(true);
    apiReferralsGet().then(setReferral).catch(() => {}).finally(() => setReferralLoading(false));
  }

  function copyReferralLink() {
    if (!referral?.link) return;
    navigator.clipboard.writeText(referral.link);
    setReferralCopied(true);
    setTimeout(() => setReferralCopied(false), 2000);
  }

  async function handleInviteFriend(e) {
    e.preventDefault(); setReferralMsg('');
    if (!friendEmail.trim()) return;
    setReferralBusy(true);
    try {
      await apiReferralsInvite(friendEmail.trim());
      setFriendEmail('');
      setReferralMsg('✓ ' + t('msg_invite_sent'));
    } catch (err) { setReferralMsg('⚠ ' + err.message); }
    finally { setReferralBusy(false); }
  }

  async function handleResendVerification() {
    setVerifyBusy(true); setVerifyMsg('');
    try {
      await apiResendVerification();
      setVerifyMsg('✓ ' + t('msg_verification_sent'));
    } catch (err) { setVerifyMsg('⚠ ' + err.message); }
    finally { setVerifyBusy(false); }
  }

  // ── Support tickets ──────────────────────────────────────
  function loadTickets() {
    setTicketsLoading(true);
    apiTicketsList().then(setTickets).catch(() => {}).finally(() => setTicketsLoading(false));
  }

  async function handleCreateTicket(e) {
    e.preventDefault(); setTicketMsg('');
    if (!newSubject.trim() || !newMessage.trim()) return;
    setTicketBusy(true);
    try {
      await apiTicketsCreate(newSubject.trim(), newMessage.trim());
      setNewSubject(''); setNewMessage(''); setShowNewTicket(false);
      setTicketMsg('✓ ' + t('msg_ticket_sent'));
      loadTickets();
    } catch (err) { setTicketMsg('⚠ ' + err.message); }
    finally { setTicketBusy(false); }
  }

  async function openTicket(id) {
    setSelectedTicket({ loading: true, id });
    try {
      const t = await apiTicketsGet(id);
      setSelectedTicket(t);
    } catch (err) { setSelectedTicket(null); }
  }

  async function handleTicketReply() {
    if (!replyText.trim() || !selectedTicket) return;
    setTicketBusy(true);
    try {
      const { ticket } = await apiTicketsReply(selectedTicket.id, replyText.trim());
      setSelectedTicket(ticket);
      setReplyText('');
      loadTickets();
    } catch (err) { alert(err.message); }
    finally { setTicketBusy(false); }
  }

  // ── 2FA ──────────────────────────────────────────────────
  async function startSetup() {
    setTfLoading(true); setTfMsg('');
    try {
      const data = await api2faSetup();
      setQrCode(data.qrCode);
      setSecret(data.secret);
      setTfStep('setup');
    } catch (e) { setTfMsg(t('prefix_error') + e.message); }
    finally { setTfLoading(false); }
  }

  async function verifyAndEnable() {
    setTfLoading(true); setTfMsg('');
    try {
      await api2faEnable(tfCode);
      setTwofa({ enabled: true });
      setTfStep('idle'); setQrCode(null); setTfCode('');
      setTfMsg('✅ ' + t('msg_2fa_enabled'));
      const d = await apiMe(); refreshUser(d.user);
    } catch (e) { setTfMsg('⚠ ' + e.message); }
    finally { setTfLoading(false); }
  }

  async function disable2fa() {
    if (!tfDisablePwd) return setTfMsg(t('msg_enter_password_disable_2fa'));
    setTfLoading(true); setTfMsg('');
    try {
      await api2faDisable(tfDisablePwd);
      setTwofa({ enabled: false }); setTfDisablePwd('');
      setTfMsg(t('msg_2fa_disabled'));
      const d = await apiMe(); refreshUser(d.user);
    } catch (e) { setTfMsg('⚠ ' + e.message); }
    finally { setTfLoading(false); }
  }

  // ── WebDAV ───────────────────────────────────────────────
  async function toggleWebdav() {
    setWdLoading(true); setWdMsg('');
    try {
      if (webdav.enabled) {
        await apiWebdavDisable();
        setWebdav({ enabled: false, token: null });
        setWdMsg(t('msg_webdav_disabled'));
      } else {
        const data = await apiWebdavEnable();
        setWebdav({ enabled: true, token: data.token });
        setShowToken(true);
        setWdMsg(t('msg_webdav_enabled'));
      }
    } catch (e) { setWdMsg('⚠ ' + e.message); }
    finally { setWdLoading(false); }
  }

  async function regenerateToken() {
    if (!window.confirm(t('confirm_regenerate_token'))) return;
    setWdLoading(true);
    try {
      const data = await apiWebdavRegenerate();
      setWebdav(prev => ({ ...prev, token: data.token }));
      setShowToken(true);
      setWdMsg(t('msg_token_regenerated'));
    } catch (e) { setWdMsg('⚠ ' + e.message); }
    finally { setWdLoading(false); }
  }

  const webdavUrl = `${SERVER_BASE}/dav/${user?.username}/`;

  // ── Native photo backup ────────────────────────────────────
  // NON SI CHIEDE PIU' LA PASSWORD (2026-09-12).
  //
  // Prima questo modulo chiedeva la password perche' il nativo doveva
  // salvarsela per ri-derivare la chiave a ogni giro. Adesso la chiave e'
  // gia' qui, in memoria, sbustata al momento dell'accesso: si consegna
  // quella. Un campo in meno da compilare, e soprattutto una password in
  // meno sul telefono.
  //
  // Chi non ha la busta non ha una chiave da consegnare: gli si dice di
  // uscire e rientrare, che e' il momento in cui la busta viene scritta.
  // LA CERIMONIA DELLA CHIAVE PRECEDE L'ATTIVAZIONE (2026-09-14, A).
  // Il backup foto carica dal telefono scavalcando il vault web, dove la
  // cerimonia vive: senza questo, un utente poteva salvare tutte le foto
  // senza avere la chiave di recupero. Qui, se la chiave non e' confermata,
  // si apre la stessa cerimonia del vault e il backup parte SOLO dopo. Se
  // l'utente la chiude, non si attiva niente e non resta nessuno stato a
  // meta'. La cerimonia registra la conferma sul server prima di richiamare
  // onFatto/onContinuaSenzaChiudere, quindi quando si arriva ad attivare la
  // chiave c'e' gia' anche lato server (il cancello B non la fermera').
  async function handleEnableBackupSubmit(e) {
    if (e && e.preventDefault) e.preventDefault();
    if (!encKey || !user?.wrappedMasterKey) { setBackupMsg('⚠ ' + t('backup_senza_busta')); return; }
    if (!user?.recoveryKeyConfirmed) { setBackupMsg(''); setCerimoniaBackup(true); return; }
    return attivaBackupDavvero();
  }

  async function attivaBackupDavvero() {
    setBackupLoading(true); setBackupMsg('');
    try {
      if (!encKey || !user?.wrappedMasterKey) {
        setBackupMsg('⚠ ' + t('backup_senza_busta'));
        return;
      }
      const perm = await requestNativeMediaPermissions();
      if (!perm.granted) {
        setBackupMsg('⚠ ' + t('backup_permission_denied'));
        return;
      }
      // Il permesso notifiche si chiede QUI, con il contesto davanti agli
      // occhi: l'utente ha appena acceso il backup foto. Se dice di no il
      // backup si accende lo stesso — negarlo per un permesso di avviso
      // sarebbe sproporzionato — ma glielo si dice, perche' senza notifiche
      // il giorno che lo spazio finisce non se ne accorgerebbe.
      const permNotifiche = await requestNativeNotificationPermission();

      const result = await enableNativeBackup(encKey, i18n.language || 'it');
      if (result.senzaBusta) {
        setBackupMsg('⚠ ' + t('backup_senza_busta'));
        return;
      }
      if (result.unavailable || result.enabled === false) {
        setBackupMsg('⚠ ' + t('backup_enable_failed'));
        return;
      }
      setBackupStatus({ enabled: true, hasMediaPermission: true });
      setBackupMsg(permNotifiche.granted || permNotifiche.nonSupportato
        ? t('backup_enabled_msg')
        : t('backup_enabled_senza_notifiche'));
    } catch (err) {
      // Il nativo rifiuta con 'senza_busta' quando la chiave non c'e' o non
      // e' della lunghezza giusta: stessa frase, stesso rimedio.
      const senzaBusta = /senza_busta/.test(String(err?.message || ''));
      setBackupMsg('⚠ ' + (senzaBusta ? t('backup_senza_busta') : (err?.message || t('backup_enable_failed'))));
    } finally {
      setBackupLoading(false);
    }
  }

  async function handleDisableBackup() {
    if (!window.confirm(t('confirm_disable_backup'))) return;
    setBackupLoading(true); setBackupMsg('');
    try {
      await disableNativeBackup();
      setBackupStatus({ enabled: false, hasMediaPermission: false });
      setBackupMsg(t('backup_disabled_msg'));
    } catch (err) {
      setBackupMsg('⚠ ' + (err?.message || t('backup_disable_failed')));
    } finally {
      setBackupLoading(false);
    }
  }

  // IL MESSAGGIO DEVE DIRE QUALCOSA (2026-09-12).
  //
  // Prima qualunque esito che non fosse "completato" diventava "Il backup non
  // e' riuscito. Riprova." — chiave assente, permesso negato, spazio finito,
  // un file illeggibile a meta' di seicento: tutto uguale. Adesso il plugin
  // manda un codice e qui si sceglie la frase; il dettaglio tecnico va in
  // console, dove serve a noi, e non a schermo, dove non serve a nessuno.
  //
  // 'gia_in_corso' NON e' un errore e non si colora di rosso: e' la risposta
  // giusta a chi preme il pulsante mentre il giro automatico sta lavorando.
  async function handleBackupNow() {
    setBackupLoading(true); setBackupMsg(''); setBackupProgress(null);
    try {
      const result = await triggerNativeBackupNow();
      if (result.lastError) console.error('backup foto:', result.codice, result.lastError);

      switch (result.codice) {
        case 'gia_in_corso':
          // Senza il ⚠: non e' un guasto. Se il giro in corso ha gia' detto a
          // che punto e', lo si dice; se non l'ha ancora detto (i primi
          // secondi), si dice solo che sta lavorando.
          setBackupMsg(result.total > 0
            ? t('backup_in_corso_con_numeri', { fatte: result.completed, totale: result.total })
            : t('backup_in_corso'));
          break;
        case 'nessuna_foto':
          setBackupMsg(t('backup_nessuna_foto'));
          break;
        case 'caricate_in_parte':
          setBackupMsg('⚠ ' + t('backup_in_parte', { fatte: result.uploaded, totale: result.total }));
          break;
        case 'fatto':
          setBackupMsg(t('backup_now_done', { count: result.uploaded }));
          break;
        case 'permesso_negato':
          setBackupMsg('⚠ ' + t('backup_permesso_negato'));
          break;
        case 'chiave_assente':
        case 'chiave_non_valida':
          setBackupMsg('⚠ ' + t('backup_chiave_assente'));
          break;
        // I CODICI DEL SERVER, non piu' un'etichetta nostra (2026-09-13).
        // Il plugin adesso restituisce il codice cosi' com'e' arrivato dal
        // server — QUOTA_EXCEEDED, FILE_LIMIT, o uno che oggi non esiste —
        // perche' la frase la sceglie il sito nella lingua dell'utente.
        // 'spazio_finito' resta per le versioni dell'app precedenti a stasera.
        // 'spazio_finito' era il codice generico delle app vecchie per il muro
        // dei file (il caso comune: 50 file del piano gratuito). Il plugin di
        // oggi risponde FILE_LIMIT; per le app vecchie si mostra la stessa
        // frase del limite file, non quella dello spazio.
        case 'FILE_LIMIT':
        case 'spazio_finito':
          setBackupMsg('⚠ ' + t('backup_file_limit', { massimo: result.maxFiles || quota?.maxFiles || '' }));
          break;
        case 'QUOTA_EXCEEDED':
          setBackupMsg('⚠ ' + t('backup_quota_exceeded'));
          break;
        case 'non_disponibile':
          setBackupMsg('⚠ ' + t('backup_enable_failed'));
          break;
        default:
          setBackupMsg('⚠ ' + t('backup_caricamento_fallito'));
      }
    } catch (err) {
      console.error('backup foto, eccezione imprevista:', err);
      setBackupMsg('⚠ ' + t('backup_caricamento_fallito'));
    } finally {
      setBackupLoading(false);
      setBackupProgress(null);
    }
  }

  // ── Email ────────────────────────────────────────────────
  async function handleUpdateEmail(e) {
    e.preventDefault(); setEmailMsg('');
    const trimmed = emailInput.trim();
    if (!trimmed || trimmed === user?.email) { setEmailEdit(false); return; }
    setEmailBusy(true);
    try {
      const data = await apiUpdateEmail(trimmed);
      refreshUser(data.user);
      setEmailEdit(false);
      setEmailMsg('✓ ' + t('msg_email_updated'));
    } catch (err) { setEmailMsg('⚠ ' + err.message); }
    finally { setEmailBusy(false); }
  }

  // ── Username ─────────────────────────────────────────────
  async function handleUpdateUsername(e) {
    e.preventDefault(); setUsernameMsg('');
    const trimmed = usernameInput.trim().toLowerCase();
    if (!trimmed || trimmed === user?.username) { setUsernameEdit(false); return; }
    setUsernameBusy(true);
    try {
      const data = await apiUpdateUsername(trimmed);
      refreshUser(data.user);
      setUsernameEdit(false);
      setUsernameMsg('✓ ' + t('msg_username_updated'));
    } catch (err) { setUsernameMsg('⚠ ' + err.message); }
    finally { setUsernameBusy(false); }
  }

  // DISABLED, NOT ORPHANED — do not "reconnect" this.
  //
  // This is the in-account password change. Nothing renders it: there is no
  // password card in the JSX below (see the matching note in the Account
  // column), so the only way back is to write that card from scratch. That
  // is deliberate, and the reason is not a UI one.
  //
  // Changing the password re-derives the AES key from (password, salt). No
  // per-file key wrapping exists, so every file uploaded before the change
  // stays encrypted under the OLD key and becomes permanently unreadable —
  // the user keeps their login and loses their files. The backend gates the
  // same path behind RECOVERY_RESET_ENABLED (backend/routes/auth.js:460),
  // which is false, so even a direct POST to /update-profile with a
  // newPassword is refused with 503; this function would fail there anyway.
  //
  // What must be true before it comes back: a wrapped-master-key scheme, so
  // that changing the password re-wraps one master key instead of re-deriving
  // the key the files were encrypted with — the migration tracked on the
  // cifratura-a-blocchi branch. When that has shipped AND is rolled out to
  // existing accounts (not just new ones), flip RECOVERY_RESET_ENABLED to
  // true, build the card, and delete this comment. Not before: re-enabling
  // it early destroys user data silently, and the user cannot get it back.
  async function saveProfile(e) {
    e.preventDefault();
    setProfileMsg(''); setProfileLoading(true);
    try {
      const updates = { firstName, lastName, email: emailVal };

      // ── PUNTO 7 di 8 (tappa 3, 2026-09-10) ────────────────────────────
      //
      // Cosa c'era qui prima, ed e' il motivo per cui il modulo non era
      // collegato a nessun pulsante: si generava un salt nuovo, si derivava
      // `newKey` dalla password nuova... e `newKey` non veniva usata. La
      // variabile finiva scartata alla riga dopo. Nel frattempo i file
      // restavano cifrati con la chiave VECCHIA, che nessuno salvava da
      // nessuna parte: chi avesse percorso questa strada sarebbe rientrato
      // nel proprio account senza poter aprire un solo file.
      //
      // Adesso la chiave dei file non cambia: cambia solo l'involucro.
      // Tutta la crittografia sta in useAuth.cambiaPassword — qui non si
      // deriva piu' niente, e non e' un caso: e' il punto in cui derivare
      // una chiave era esattamente la cosa sbagliata da fare.
      if (newPwd) {
        if (newPwd !== newPwdConf) { setProfileMsg('⚠ ' + t('msg_passwords_mismatch')); setProfileLoading(false); return; }
        if (newPwd.length < 8)    { setProfileMsg('⚠ ' + t('msg_min_chars', {count: 8})); setProfileLoading(false); return; }
        const data = await cambiaPassword(curPwd, newPwd, updates);
        localStorage.setItem('zv_user', JSON.stringify(data.user));
        setProfileMsg('✅ ' + t('msg_profile_updated'));
        setProfileEdit(false);
        setCurPwd(''); setNewPwd(''); setNewPwdConf('');
        setProfileLoading(false);
        return;
      }

      const data = await apiUpdateProfile(updates);
      localStorage.setItem('zv_user', JSON.stringify(data.user));
      setProfileMsg('✅ ' + t('msg_profile_updated'));
      setProfileEdit(false);
      setCurPwd(''); setNewPwd(''); setNewPwdConf('');
    } catch(e) { setProfileMsg('⚠ ' + e.message); }
    finally { setProfileLoading(false); }
  }

  async function handleCancelSubscription() {
    if (!window.confirm(t('confirm_cancel_autorenew'))) return;
    setSubLoading(true); setSubMsg('');
    try {
      const data = await apiCancelSubscription();
      setSubMsg('✅ ' + data.message);
      if (refreshUser) refreshUser();
    } catch(e) { setSubMsg('⚠ ' + e.message); }
    finally { setSubLoading(false); }
  }

  async function handleResumeSubscription() {
    setSubLoading(true); setSubMsg('');
    try {
      const data = await apiResumeSubscription();
      setSubMsg('✅ ' + data.message);
      if (refreshUser) refreshUser();
    } catch(e) { setSubMsg('⚠ ' + e.message); }
    finally { setSubLoading(false); }
  }

  async function generateNewRecoveryToken() {
    if (!encKey) return setRecoveryMsg('⚠ ' + t('msg_session_expired'));

    // LA CONFERMA, SOLO PER CHI UNA CHIAVE CE L'HA GIA' (2026-09-12).
    //
    // /recovery/setup SOSTITUISCE la chiave precedente nell'istante in cui
    // si preme, e la scrittura avviene PRIMA che la nuova venga mostrata:
    // chi chiude la scheda in quel mezzo secondo resta senza nessuna via di
    // recupero, e non se ne accorge. Due schede piu' su, il pulsante che
    // rigenera il token WebDAV una conferma ce l'ha gia' — e quello rompe
    // soltanto delle connessioni rclone.
    // Alla prima generazione non si chiede niente: non c'e' niente da
    // perdere, e una conferma li' sarebbe solo un ostacolo.
    if (user?.hasRecoveryToken && !window.confirm(t('confirm_regenerate_recovery_key'))) return;
    setRecoveryLoading(true); setRecoveryMsg('');
    try {
      const token  = generateRecoveryToken();
      const salt   = user?.salt || (await apiMe()).salt;
      if (!salt) throw new Error(t('err_salt_not_found'));
      const rKey   = await deriveRecoveryKey(token, salt);
      const backup = await encryptKeyWithRecovery(encKey, rKey);
      // Send raw token - backend bcrypts it
      await apiRecoverySetup(token, backup);
      setRecoveryToken(token);
      setRecoveryMsg('success');


    } catch(e) { setRecoveryMsg('⚠ ' + t('prefix_error') + e.message); }
    finally { setRecoveryLoading(false); }
  }

  function copyRecovery() {
    navigator.clipboard.writeText(recoveryToken);
    setRecoveryCopied(true);
    setTimeout(() => setRecoveryCopied(false), 2000);
  }

  // COPIA DEI DATI WEBDAV, CON CONFERMA (2026-09-06).
  //
  // Prima i tre pulsanti 📋 copiavano in silenzio. Sul token era peggio che
  // altrove: il token e' sfocato, quindi non cambiava NIENTE sullo schermo e
  // non c'era modo di capire se fosse successo qualcosa — la reazione normale
  // e' cliccare di nuovo, o rinunciare e trascrivere 73 caratteri a mano.
  //
  // Tre cose insieme, non una:
  //   1. il pulsante diventa ✓ e accanto compare "Copiato!" per due secondi;
  //   2. copiando il TOKEN lo si mostra anche in chiaro, cosi' si vede cosa
  //      e' finito negli appunti e lo si puo' confrontare;
  //   3. si aspetta l'esito. writeText FALLISCE senza dire niente fuori dai
  //      contesti sicuri e quando il permesso e' negato: confermare prima di
  //      sapere se e' andata sarebbe peggio del silenzio di adesso, perche'
  //      direbbe una cosa falsa. Se fallisce si seleziona il testo, cosi'
  //      resta comunque un Ctrl+C.
  async function copiaWebdav(quale, testo, elemento) {
    try {
      await navigator.clipboard.writeText(testo);
      if (quale === 'token') setShowToken(true);
      setWdCopiato(quale);
      setTimeout(() => setWdCopiato(c => (c === quale ? null : c)), 2000);
    } catch {
      setWdCopiato(null);
      try {
        const nodo = elemento?.current;
        if (nodo) {
          const r = document.createRange(); r.selectNodeContents(nodo);
          const s = window.getSelection(); s.removeAllRanges(); s.addRange(r);
        }
      } catch { /* niente selezione: resta comunque il testo visibile */ }
      setWdMsg(t('msg_copia_fallita'));
      setTimeout(() => setWdMsg(''), 4000);
    }
  }

  return (
    <div className="profile-page">
      <header className="profile-header">
        <h2>{t('page_title')}</h2>
        <button type="button" className="profile-back-link" onClick={onBack}>
          ← {t('btn_back_to_vault')}
        </button>
      </header>

      {/* Two columns above 1024px, one below — see the PROFILE block in
          App.css. Account settings left, everything that involves other
          people right; the wide sections below keep the full container. */}
      <div className="profile-grid">
        <div className="profile-col">
          <h3 className="profile-col-title">{t('col_account')}</h3>

          {/* ── LANGUAGE ─────────────────────────────────────── */}
          <div className="profile-card">
            <div className="profile-card-header">
              <div><h3>{t('language_label')}</h3></div>
              <LanguageSwitcher />
            </div>
          </div>

          {/* ── EMAIL ────────────────────────────────────────── */}
          <div className="profile-card">
            <div className="profile-card-header">
              <div>
                <h3>{t('email_title')}</h3>
                <p>{t('email_desc')}</p>
              </div>
              <span className={`status-badge ${user?.emailVerified ? 'on' : 'off'}`}>
                {user?.emailVerified ? `✅ ${t('badge_verified')}` : `⚠ ${t('badge_unverified')}`}
              </span>
            </div>

            {!emailEdit ? (
              <div className="webdav-url-row">
                <span className="webdav-label">{t('email_current_label')}</span>
                <code className="webdav-url">{user?.email || '—'}</code>
                <button className="btn-regen" onClick={() => { setEmailInput(user?.email || ''); setEmailEdit(true); setEmailMsg(''); }}>
                  ✏️ {t('btn_change')}
                </button>
              </div>
            ) : (
              <form onSubmit={handleUpdateEmail} className="family-invite-form">
                <input type="email" value={emailInput} onChange={e => setEmailInput(e.target.value)}
                  placeholder={t('email_placeholder')} required autoFocus />
                <button type="submit" className="btn-enable-2fa" disabled={emailBusy}>
                  {emailBusy ? '…' : t('btn_save')}
                </button>
                <button type="button" className="btn-link-back" onClick={() => { setEmailEdit(false); setEmailMsg(''); }}>
                  {t('btn_cancel')}
                </button>
              </form>
            )}

            {!user?.emailVerified && !emailEdit && (
              <p className="webdav-hint">
                {t('email_unverified_hint')}{' '}
                <button className="btn-link-back" style={{ display: 'inline', padding: 0 }} onClick={handleResendVerification} disabled={verifyBusy}>
                  {verifyBusy ? t('status_sending') : t('btn_resend_verification')}
                </button>
              </p>
            )}

            {emailMsg && (
              <div className={`profile-msg ${emailMsg.startsWith('⚠') ? 'error' : 'success'}`}>{emailMsg}</div>
            )}
          </div>

          {/* ── USERNAME ─────────────────────────────────────── */}
          <div className="profile-card">
            <div className="profile-card-header">
              <div>
                <h3>{t('username_title')}</h3>
                <p>{t('username_desc')}</p>
              </div>
            </div>

            {!usernameEdit ? (
              <div className="webdav-url-row">
                <span className="webdav-label">{t('username_current_label')}</span>
                <code className="webdav-url">{user?.username || '—'}</code>
                <button className="btn-regen" onClick={() => { setUsernameInput(user?.username || ''); setUsernameEdit(true); setUsernameMsg(''); }}>
                  ✏️ {t('btn_change')}
                </button>
              </div>
            ) : (
              <form onSubmit={handleUpdateUsername} className="family-invite-form">
                <input type="text" value={usernameInput} onChange={e => setUsernameInput(e.target.value)}
                  placeholder={t('username_placeholder')} minLength={3} maxLength={32}
                  pattern="[a-zA-Z0-9_]+" title={t('username_pattern_hint')} required autoFocus />
                <button type="submit" className="btn-enable-2fa" disabled={usernameBusy}>
                  {usernameBusy ? '…' : t('btn_save')}
                </button>
                <button type="button" className="btn-link-back" onClick={() => { setUsernameEdit(false); setUsernameMsg(''); }}>
                  {t('btn_cancel')}
                </button>
              </form>
            )}

            {usernameEdit && (
              <p className="webdav-hint">{t('username_pattern_hint')}</p>
            )}

            {usernameMsg && (
              <div className={`profile-msg ${usernameMsg.startsWith('⚠') ? 'error' : 'success'}`}>{usernameMsg}</div>
            )}
          </div>

          {/* ── 2FA Section ─────────────────────────────────── */}
          <div className="profile-card">
            <div className="profile-card-header">
              <div>
                <h3>{t('twofa_title')}</h3>
                <p>{t('twofa_desc')}</p>
              </div>
              <span className={`status-badge ${twofa.enabled ? 'on' : 'off'}`}>
                {twofa.enabled ? `✅ ${t('badge_enabled')}` : `⚪ ${t('badge_disabled')}`}
              </span>
            </div>

            {!twofa.enabled && tfStep === 'idle' && (
              <button className="btn-enable-2fa" onClick={startSetup} disabled={tfLoading}>
                {tfLoading ? t('status_loading') : `+ ${t('btn_enable_2fa')}`}
              </button>
            )}

            {tfStep === 'setup' && qrCode && (
              <div className="twofa-setup">
                <p className="twofa-instruction">
                  {t('twofa_step1')}<br/>
                  {t('twofa_step2')}<br/>
                  {t('twofa_step3')}
                </p>
                <div className="qr-container">
                  <img src={qrCode} alt={t('qr_alt')} className="qr-img" />
                </div>
                <div className="secret-key">
                  <span className="secret-label">{t('secret_label')}</span>
                  <code className="secret-value">{secret}</code>
                </div>
                <div className="field" style={{marginTop: 16}}>
                  <label>{t('verification_code_label')}</label>
                  <input
                    type="text" inputMode="numeric" maxLength={6}
                    value={tfCode} onChange={e => setTfCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="123456" className="code-field"
                    autoFocus
                  />
                </div>
                <div className="twofa-actions">
                  <button className="btn-cancel" onClick={() => { setTfStep('idle'); setQrCode(null); setTfCode(''); }}>{t('btn_cancel')}</button>
                  <button className="btn-primary-sm" onClick={verifyAndEnable} disabled={tfLoading || tfCode.length !== 6}>
                    {tfLoading ? t('status_verifying') : `✓ ${t('btn_confirm_enable')}`}
                  </button>
                </div>
              </div>
            )}

            {twofa.enabled && (
              <div className="twofa-disable">
                <p className="twofa-instruction">{t('twofa_disable_instruction')}</p>
                <div className="field">
                  <input
                    type="password" value={tfDisablePwd}
                    onChange={e => setTfDisablePwd(e.target.value)}
                    placeholder={t('password_placeholder')} className="code-field"
                  />
                </div>
                <button className="btn-danger-sm" onClick={disable2fa} disabled={tfLoading}>
                  {tfLoading ? t('status_disabling') : `🗑 ${t('btn_disable_2fa')}`}
                </button>
              </div>
            )}

            {tfMsg && (
              <div className={`profile-msg ${tfMsg.startsWith('⚠') ? 'error' : 'success'}`}>{tfMsg}</div>
            )}
          </div>

          {/* ── PASSWORD (punto 7 di 8, tappa 3) ────────────────
              La decisione arriva dal SERVER (user.puoCambiarePassword,
              safeUser in routes/auth.js, 2026-09-16): interruttore, elenco,
              busta verificata. Niente variabili di build.
              Senza la busta la sua chiave e' ancora PBKDF2(password, salt) e
              cambiare password gliela porterebbe via: il server rifiuta con
              409, ma un pulsante che porta a un rifiuto e' un pulsante che
              genera un ticket. Oggi sono 462 account su 463 a non averla, e
              nessuno di loro deve vedere questa scheda. */}
          {user?.puoCambiarePassword && (
          <div className="profile-card">
            <div className="profile-card-header">
              <div>
                <h3>{t('password_title')}</h3>
                <p>{t('password_desc')}</p>
              </div>
              <span className="status-badge on">✅ {t('password_badge_ready')}</span>
            </div>

            <form onSubmit={saveProfile} className="family-invite-form" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
              <input type="password" value={curPwd} onChange={e => setCurPwd(e.target.value)}
                placeholder={t('password_current_label')} autoComplete="current-password" required />
              <input type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)}
                placeholder={t('password_new_label')} autoComplete="new-password" required minLength={8} />
              <input type="password" value={newPwdConf} onChange={e => setNewPwdConf(e.target.value)}
                placeholder={t('password_confirm_label')} autoComplete="new-password" required minLength={8} />
              <button type="submit" className="btn-enable-2fa" disabled={profileLoading || !curPwd || !newPwd}>
                {profileLoading ? t('password_working') : t('btn_change_password')}
              </button>
            </form>

            {profileMsg && (
              <div className={`profile-msg ${profileMsg.startsWith('⚠') ? 'error' : 'success'}`}>{profileMsg}</div>
            )}
          </div>
          )}

          {/* ── RECOVERY TOKEN ──────────────────────────────── */}
          <div className="profile-card">
            <div className="profile-card-header">
              <div>
                <h3>{t('recovery_title')}</h3>
                <p>{t('recovery_desc')}</p>
              </div>
              <span className={`status-badge ${user?.hasRecoveryToken ? 'on' : 'off'}`}>
                {user?.hasRecoveryToken ? `✅ ${t('badge_recovery_configured')}` : `⚠ ${t('badge_recovery_not_configured')}`}
              </span>
            </div>

            {!recoveryToken && (
              <button className="btn-enable-2fa" onClick={generateNewRecoveryToken} disabled={recoveryLoading}>
                {recoveryLoading ? t('status_generating') : user?.hasRecoveryToken ? `🔄 ${t('btn_regenerate_key')}` : `+ ${t('btn_generate_recovery_key')}`}
              </button>
            )}

            {recoveryToken && recoveryMsg === 'success' && (
              <div className="rt-section">
                <div className="rt-alert">
                  ⚠️ <strong>{t('recovery_alert_strong')}</strong> {t('recovery_alert_rest')}
                </div>
                <div className="rt-box">
                  <div className="rt-token">{recoveryToken}</div>
                  <button className={`rt-copy ${recoveryCopied?'copied':''}`} onClick={copyRecovery}>
                    {recoveryCopied ? `✓ ${t('status_copied')}` : `📋 ${t('btn_copy')}`}
                  </button>
                </div>
                <div className="rt-instructions">
                  <div className="rt-warn-item">🔒 {t('rt_instruction_password_manager')}</div>
                  <div className="rt-warn-item">📄 {t('rt_instruction_print')}</div>
                  <div className="rt-warn-item">❌ {t('rt_instruction_lost')}</div>
                </div>
                <div style={{display:'flex', gap:10, flexWrap:'wrap'}}>
                  <button className="btn-enable-2fa" onClick={() => {
                    const content = [
                      t('recovery_file_title'),
                      '================================',
                      '',
                      t('recovery_file_account_label') + (user?.username || ''),
                      t('recovery_file_date_label') + formatDateTime(new Date(), i18n.language),
                      '',
                      t('recovery_file_key_header'),
                      recoveryToken,
                      '',
                      '================================',
                      t('recovery_file_footer1'),
                      t('recovery_file_footer2'),
                      t('recovery_file_footer3'),
                      '================================',
                    ].join('\n');
                    const blob = new Blob([content], { type: 'text/plain' });
                    const url  = URL.createObjectURL(blob);
                    const a    = document.createElement('a');
                    a.href = url;
                    a.download = `lifetimecloud-recovery-${user?.username || 'key'}.txt`;
                    document.body.appendChild(a); a.click();
                    document.body.removeChild(a);
                    setTimeout(() => URL.revokeObjectURL(url), 1000);
                  }}>
                    ⬇ {t('btn_download_txt')}
                  </button>
                  <button className="btn-primary-sm" onClick={() => { setRecoveryToken(''); setRecoveryMsg(''); }}>
                    ✓ {t('btn_saved_key')}
                  </button>
                </div>
              </div>
            )}

            {recoveryMsg && recoveryMsg !== 'success' && (
              <div className={`profile-msg ${recoveryMsg.startsWith('⚠') ? 'error' : 'success'}`}>{recoveryMsg}</div>
            )}
          </div>
        </div>

        <div className="profile-col">
          <h3 className="profile-col-title">{t('col_sharing')}</h3>

          {/* ── REFERRAL PROGRAM ────────────────────────────── */}
          {!referralLoading && referral && (
            <div className="profile-card">
              <div className="profile-card-header">
                <div>
                  <h3>{t('referral_title')}</h3>
                  <p>{t('referral_desc', {amount: referral.discountAmount})}</p>
                </div>
                <span className="status-badge on">
                  {t('referral_earned_badge', {amount: formatBytes(referral.bonusBytes)})}
                </span>
              </div>

              {!user?.emailVerified && (
                <div className="profile-msg warn">
                  ⚠ {t('referral_unverified_hint')}{' '}
                  <button className="btn-link-back" style={{ display: 'inline', padding: 0 }} onClick={handleResendVerification} disabled={verifyBusy}>
                    {verifyBusy ? t('status_sending') : t('btn_resend_verification')}
                  </button>
                </div>
              )}

              {referral.wasReferred && referral.discountEligible && (
                <div className="profile-msg success">
                  🎉 {t('referral_was_referred_msg', {amount: referral.discountAmount})}
                </div>
              )}

              <div className="webdav-url-row">
                <span className="webdav-label">{t('referral_link_label')}</span>
                <code className="webdav-url">{referral.link}</code>
                <button className="btn-copy" onClick={copyReferralLink} title={t('btn_copy')}>{referralCopied ? '✓' : '📋'}</button>
              </div>

              <p className="family-hint">{t('referral_used_count', {used: referral.rewardedCount, cap: referral.cap})}</p>

              <form onSubmit={handleInviteFriend} className="family-invite-form">
                <input type="email" value={friendEmail} onChange={e => setFriendEmail(e.target.value)}
                  placeholder={t('friend_email_placeholder')} required />
                <button type="submit" className="btn-enable-2fa" disabled={referralBusy}>
                  {referralBusy ? '…' : `+ ${t('btn_send_invite')}`}
                </button>
              </form>

              {referral.log.length > 0 && (
                <div className="profile-info-grid">
                  {referral.log.map((r, i) => (
                    <div key={i} className="profile-info-row family-member-row">
                      <span>{r.email}</span>
                      <span className="family-member-used">
                        {r.status === 'rewarded' ? `✅ +${formatBytes(r.bonusBytes)}`
                          : r.status === 'pending' ? `⏳ ${t('referral_status_pending')}`
                          : `⚪ ${t('referral_status_not_rewarded')}`}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {(referralMsg || verifyMsg) && (
                <div className={`profile-msg ${(referralMsg || verifyMsg).startsWith('⚠') ? 'error' : 'success'}`}>{referralMsg || verifyMsg}</div>
              )}
            </div>
          )}

          {/* ── FAMILY ───────────────────────────────────────── */}
          <FamilyPanel titleKey="panel_title_plain" />
        </div>
      </div>

      <div className="profile-wide">
          {/* ── WebDAV / rclone Section ───────────────────────── */}
          <div className="profile-card">
            <div className="profile-card-header">
              <div>
                <h3>{t('webdav_title')}</h3>
                <p>{t('webdav_desc')}</p>
              </div>
              <span className={`status-badge ${webdav.enabled ? 'on' : 'off'}`}>
                {webdav.enabled ? `✅ ${t('badge_enabled')}` : `⚪ ${t('badge_disabled')}`}
              </span>
            </div>

            {/* PERCHE' STA QUI E IN EVIDENZA (2026-09-06): il 12,5% dei file
                nel sistema risulta non cifrato, e vengono tutti da questa
                strada. Chi attiva WebDAV deve saperlo PRIMA di caricare, non
                scoprirlo da un'etichetta nel file manager. */}
            <div className="webdav-avviso-cifratura">
              <span className="webdav-avviso-icona" aria-hidden="true">🔓</span>
              <div>
                <strong>{t('webdav_avviso_titolo')}</strong>
                <p>{t('webdav_avviso_testo')}</p>
              </div>
            </div>

            <div className="webdav-toggle-row">
              <button
                className={webdav.enabled ? 'btn-danger-sm' : 'btn-enable-2fa'}
                onClick={toggleWebdav} disabled={wdLoading}
              >
                {wdLoading ? '…' : webdav.enabled ? t('btn_disable_webdav') : `+ ${t('btn_enable_webdav')}`}
              </button>
              {webdav.enabled && (
                <button className="btn-regen" onClick={regenerateToken} disabled={wdLoading}>
                  🔄 {t('btn_regenerate_token')}
                </button>
              )}
            </div>

            {webdav.enabled && (
              <div className="webdav-info">
                <div className="webdav-url-row">
                  <span className="webdav-label">{t('webdav_url_label')}</span>
                  <code className="webdav-url" ref={rifUrl}>{webdavUrl}</code>
                  <button className={`btn-copy ${wdCopiato === 'url' ? 'copied' : ''}`}
                    onClick={() => copiaWebdav('url', webdavUrl, rifUrl)} title={t('btn_copy')}>
                    {wdCopiato === 'url' ? '✓' : '📋'}
                  </button>
                  {wdCopiato === 'url' && <span className="webdav-copiato">{t('status_copied')}</span>}
                </div>
                <div className="webdav-url-row">
                  <span className="webdav-label">{t('webdav_username_label')}</span>
                  <code className="webdav-url">{user?.username}</code>
                </div>
                <div className="webdav-url-row">
                  <span className="webdav-label">{t('webdav_password_label')}</span>
                  <code className="webdav-url token-blur" ref={rifToken}
                    style={{ filter: showToken ? 'none' : 'blur(5px)', cursor: 'pointer' }}
                    onClick={() => setShowToken(t => !t)}>
                    {webdav.token}
                  </code>
                  {/* Copiando il token lo si mostra anche in chiaro: se resta
                      sfocato non cambia niente sullo schermo e non si capisce
                      che e' successo qualcosa. */}
                  <button className={`btn-copy ${wdCopiato === 'token' ? 'copied' : ''}`}
                    onClick={() => copiaWebdav('token', webdav.token, rifToken)} title={t('btn_copy')}>
                    {wdCopiato === 'token' ? '✓' : '📋'}
                  </button>
                  {wdCopiato === 'token' && <span className="webdav-copiato">{t('status_copied')}</span>}
                </div>
                <p className="webdav-hint">{t('webdav_hint')}</p>

                {/* LE GUIDE, QUI SOTTO (2026-09-06). Chi sta guardando questi
                    tre campi e' esattamente chi deve collegare il computer:
                    prima doveva indovinare da solo cosa farne.
                    Per primo il sistema che il browser dichiara — non per
                    nasconderne uno, ma perche' su un Mac la prima riga deve
                    essere quella del Mac. Se non si capisce, resta l'ordine
                    Mac/Windows. */}
                <div className="webdav-guide">
                  <span className="webdav-guide-titolo">{t('webdav_guide_titolo')}</span>
                  <div className="webdav-guide-link">
                    {(sistemaOperativo() === 'windows' ? ['windows', 'mac'] : ['mac', 'windows']).map(quale => (
                      <button key={quale} type="button" onClick={() => onApriGuida?.(quale)}>
                        {quale === 'mac' ? t('webdav_guide_mac') : t('webdav_guide_windows')}
                        <span aria-hidden="true"> →</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="rclone-snippet">
                  <div className="snippet-header">
                    <span>{t('rclone_config_example')}</span>
                    <button className={`btn-copy ${wdCopiato === 'rclone' ? 'copied' : ''}`}
                      onClick={() => copiaWebdav('rclone',
    `[lifetimecloud]
    type = webdav
    url = ${webdavUrl}
    vendor = other
    user = ${user?.username}
    pass = ${webdav.token || 'YOUR_TOKEN'}`
                      )}>
                      {wdCopiato === 'rclone' ? `✓ ${t('status_copied')}` : `📋 ${t('btn_copy')}`}
                    </button>
                  </div>
                  <pre className="snippet-code">{`[lifetimecloud]
    type = webdav
    url = ${webdavUrl}
    vendor = other
    user = ${user?.username}
    pass = YOUR_TOKEN  ← use: rclone obscure YOUR_TOKEN`}</pre>
                </div>

                <div className="rclone-snippet">
                  <div className="snippet-header"><span>{t('rclone_sync_example')}</span></div>
                  <pre className="snippet-code">{`# Upload a folder
    rclone sync /local/folder lifetimecloud:MyFolder

    # List files
    rclone ls lifetimecloud:

    # Mount as drive (Linux/Mac)
    rclone mount lifetimecloud: ~/LifetimeCloud --daemon`}</pre>
                </div>
              </div>
            )}

            {wdMsg && (
              <div className={`profile-msg ${wdMsg.startsWith('⚠') ? 'error' : 'success'}`}>{wdMsg}</div>
            )}
          </div>

          {/* ── Native photo backup (Android app only) ─────────── */}
          {isNative && (
            <div className="profile-card">
              <div className="profile-card-header">
                <div>
                  <h3>{t('backup_title')}</h3>
                  <p>{t('backup_desc')}</p>
                  {/* CHI LO ACCENDE DEVE SAPERE COSA STA ACCENDENDO
                      (2026-09-12). Il backup non e' solo "le foto si
                      caricano da sole": tiene la chiave su questo
                      dispositivo, e da quella chiave dipende anche lo
                      sblocco senza password. Un interruttore solo, due
                      effetti — e vanno detti tutti e due prima, non
                      scoperti dopo. */}
                  <p className="webdav-hint">{t('backup_sblocco_nota')}</p>
                </div>
                <span className={`status-badge ${backupStatus.enabled ? 'on' : 'off'}`}>
                  {backupStatus.enabled ? `✅ ${t('badge_enabled')}` : `⚪ ${t('badge_disabled')}`}
                </span>
              </div>

              {/* Un pulsante solo: la chiave e' gia' in memoria, non c'e'
                  niente da digitare. Il modulo con la password e' stato
                  tolto il 2026-09-12 insieme alla password nel Keystore. */}
              {!backupStatus.enabled && (
                <div className="webdav-toggle-row">
                  <button className="btn-enable-2fa" onClick={handleEnableBackupSubmit} disabled={backupLoading}>
                    {backupLoading ? '…' : `+ ${t('btn_enable_backup')}`}
                  </button>
                </div>
              )}



              {backupStatus.enabled && (
                <div className="webdav-toggle-row">
                  {/* Premere "Esegui backup ora" quando l'account e' contro un
                      muro non fa niente di utile: il motore controlla spazio e
                      numero file e si ferma subito. Un tasto che non puo'
                      riuscire non deve essere premibile — e deve dire perche'.
                      La ragione sta sotto, non dentro il tasto: nel tasto
                      starebbe stretta e la si leggerebbe come un'etichetta. */}
                  <button className="btn-enable-2fa" onClick={handleBackupNow}
                          disabled={backupLoading || accountAlMuro}>
                    {backupLoading ? '…' : `🔄 ${t('btn_backup_now')}`}
                  </button>
                  <button className="btn-danger-sm" onClick={handleDisableBackup} disabled={backupLoading}>
                    {t('btn_disable_backup')}
                  </button>
                </div>
              )}

              {accountAlMuro && (
                <p className="webdav-hint">
                  {muroFile
                    ? t('backup_fermo_file', { massimo: quota.maxFiles })
                    : t('backup_fermo_spazio')}
                </p>
              )}

              {backupProgress && (
                <p className="webdav-hint">
                  {t('backup_progress', { completed: backupProgress.completed, total: backupProgress.total })}
                  {backupProgress.currentName ? ` — ${backupProgress.currentName}` : ''}
                </p>
              )}

              {backupMsg && (
                <div className={`profile-msg ${backupMsg.startsWith('⚠') ? 'error' : 'success'}`}>{backupMsg}</div>
              )}
              {cerimoniaBackup && (
                <CerimoniaChiave
                  onAnnulla={() => setCerimoniaBackup(false)}
                  onFatto={() => { setCerimoniaBackup(false); attivaBackupDavvero(); }}
                  onContinuaSenzaChiudere={() => { setCerimoniaBackup(false); attivaBackupDavvero(); }}
                />
              )}
            </div>
          )}

          {/* ── ABBONAMENTO ──────────────────────────────────── */}
          {false && (
            <div className="profile-card">
              <div className="profile-card-header">
                <div>
                  <h3>{t('subscription_title')}</h3>
                  <p>{t('subscription_desc')}</p>
                </div>
                <span className={`status-badge ${user?.autoRenew === false ? 'off' : 'on'}`}>
                  {user?.autoRenew === false ? `⏸ ${t('badge_autorenew_off')}` : `✅ ${t('badge_autorenew_on')}`}
                </span>
              </div>

              {user?.planExpiresAt && (
                <p style={{fontSize:13,color:'#8b949e',marginBottom:8}}>
                  {t('subscription_plan_until')} <strong style={{color:'#e2e8f0'}}>{formatDate(user.planExpiresAt, i18n.language)}</strong>
                </p>
              )}

              {user?.autoRenew === false ? (
                <div>
                  <p style={{fontSize:13,color:'#f87171',marginBottom:12}}>
                    ⚠ {t('subscription_autorenew_disabled_warning')}
                  </p>
                  <button className="btn-enable-2fa" onClick={handleResumeSubscription} disabled={subLoading}>
                    {subLoading ? t('status_please_wait') : `▶ ${t('btn_reenable_autorenew')}`}
                  </button>
                </div>
              ) : (
                <button className="btn-logout" onClick={handleCancelSubscription} disabled={subLoading}>
                  {subLoading ? t('status_please_wait') : `⏸ ${t('btn_cancel_autorenew')}`}
                </button>
              )}

              {subMsg && <div className={`profile-msg ${subMsg.startsWith('⚠')?'error':'success'}`} style={{marginTop:10}}>{subMsg}</div>}
            </div>
          )}

          {/* ── Support Tickets Section ──────────────────────── */}
          <div className="profile-card">
            <div className="profile-card-header">
              <div>
                <h3>{t('support_title')}</h3>
                <p>{t('support_desc')}</p>
              </div>
              {!showNewTicket && (
                <button className="btn-enable-2fa" onClick={() => { setShowNewTicket(true); setSelectedTicket(null); setTicketMsg(''); }}>
                  + {t('btn_new_ticket')}
                </button>
              )}
            </div>

            {showNewTicket && (
              <form className="ticket-new-form" onSubmit={handleCreateTicket}>
                <input
                  className="tf-input" placeholder={t('ticket_subject_placeholder')} maxLength={200}
                  value={newSubject} onChange={e => setNewSubject(e.target.value)}
                />
                <textarea
                  className="ticket-textarea" placeholder={t('ticket_message_placeholder')} maxLength={5000}
                  value={newMessage} onChange={e => setNewMessage(e.target.value)}
                />
                <div style={{ display: 'flex', gap: 10 }}>
                  <button type="button" className="btn-link-back" onClick={() => setShowNewTicket(false)}>{t('btn_cancel')}</button>
                  <button type="submit" className="btn-enable-2fa" disabled={ticketBusy || !newSubject.trim() || !newMessage.trim()}>
                    {ticketBusy ? t('status_sending') : t('btn_send_ticket')}
                  </button>
                </div>
              </form>
            )}

            {ticketMsg && (
              <div className={`profile-msg ${ticketMsg.startsWith('⚠') ? 'error' : 'success'}`}>{ticketMsg}</div>
            )}

            {selectedTicket && (
              <div className="ticket-thread">
                <button className="btn-link-back" onClick={() => setSelectedTicket(null)}>← {t('btn_back_to_tickets')}</button>
                {selectedTicket.loading ? (
                  <p style={{ color: 'var(--text3)', fontSize: 13 }}>{t('status_loading')}</p>
                ) : (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                      <strong style={{ fontSize: 14 }}>{selectedTicket.code} — {selectedTicket.subject}</strong>
                      <span className={`status-pill ${selectedTicket.status}`}>{selectedTicket.status.replace('_', ' ')}</span>
                    </div>
                    {selectedTicket.messages.map(m => (
                      <div key={m.id} className={`ticket-msg from-${m.from}`}>
                        <div className="ticket-msg-meta">{m.from === 'admin' ? t('ticket_from_support') : t('ticket_from_you')} · {formatDateTime(m.createdAt, i18n.language)}</div>
                        {m.body}
                      </div>
                    ))}
                    <textarea
                      className="ticket-textarea" placeholder={t('ticket_reply_placeholder')}
                      value={replyText} onChange={e => setReplyText(e.target.value)}
                    />
                    <button className="btn-enable-2fa" style={{ alignSelf: 'flex-start' }} onClick={handleTicketReply} disabled={ticketBusy || !replyText.trim()}>
                      {ticketBusy ? t('status_sending') : t('btn_reply')}
                    </button>
                  </>
                )}
              </div>
            )}

            {!selectedTicket && !ticketsLoading && (
              <div className="ticket-list">
                {tickets.length === 0 && !showNewTicket && (
                  <p style={{ color: 'var(--text3)', fontSize: 13 }}>{t('empty_no_tickets')}</p>
                )}
                {tickets.map(t => (
                  <div key={t.id} className="ticket-row" onClick={() => openTicket(t.id)}>
                    <span className="ticket-code">{t.code}</span>
                    <span className="ticket-subject">{t.subject}</span>
                    <span className="ticket-date">{formatDate(t.updatedAt, i18n.language)}</span>
                    <span className={`status-pill ${t.status}`}>{t.status.replace('_', ' ')}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
      </div>
    </div>
  );
}
